import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import { usePortfolio } from "../context/PortfolioContext";
import { fmtINR, fmtUnits, fmtPct } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Search, ArrowDownRight, ArrowUpRight, Loader2, ChevronRight } from "lucide-react";
import { stampedName } from "../lib/csv";
import ExportButtons from "../components/ExportButtons";
import { computeMemberMetrics } from "../lib/xirr";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
} from "recharts";

// Palette for donut (up to 12 distinct hues)
const PALETTE = [
  "#10b981", "#6366f1", "#f59e0b", "#ec4899", "#06b6d4", "#84cc16",
  "#f43f5e", "#8b5cf6", "#0ea5e9", "#f97316", "#22d3ee", "#a855f7",
];

export default function Members() {
  const { user } = useAuth();
  const { members, transactions, loading } = usePortfolio();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState("holdingPct");

  // Compute XIRR + CAGR once per member (memoised on members/transactions change).
  const enriched = useMemo(() => {
    return members.map((m) => ({
      ...m,
      _metrics: computeMemberMetrics(m, transactions),
    }));
  }, [members, transactions]);

  const sorted = useMemo(() => {
    const filtered = enriched.filter((m) =>
      m.name.toLowerCase().includes(q.toLowerCase()),
    );
    const accessor = (m) => {
      if (sortKey === "xirr") return m._metrics?.xirr ?? -Infinity;
      if (sortKey === "cagr") return m._metrics?.cagr ?? -Infinity;
      return m[sortKey] || 0;
    };
    return filtered.sort((a, b) => accessor(b) - accessor(a));
  }, [enriched, q, sortKey]);

  // Build allocation pie data (present value contribution)
  const pieData = useMemo(() => {
    return members
      .filter((m) => (m.currentValue || 0) > 0)
      .map((m, i) => ({
        name: m.name,
        value: m.currentValue || 0,
        fill: PALETTE[i % PALETTE.length],
      }));
  }, [members]);

  // Per-member cumulative-value timeline moved to MemberDetail page.

  const isAdmin = user?.role === "admin";
  const exportData = useMemo(() => {
    const headers = [
      "Member", "Capital Invested", "Units", "Avg NAV", "Capital Current Value", "Capital Gain/Loss", "Capital Return %",
      "Bonus Units", "Bonus Capital", "Bonus Value", "Bonus Gain", "Bonus Gain %",
      "Total Units", "Overall Current Value", "Overall Gain/Loss", "Overall %", "Holding %",
      "XIRR %", "CAGR %", "Years",
    ];
    const rows = sorted.map((m) => [
      m.name, m.capitalInvested, m.units, m.avgNAV, m.currentValue, m.gainLoss, (m.gainLossPct || 0).toFixed(2),
      m.bonusUnits || 0, m.bonusCapital || 0, m.bonusValue || 0, m.bonusGainLoss || 0, (m.bonusGainPct || 0).toFixed(2),
      m.overallUnits || m.units, m.overallCurrentValue || m.currentValue, m.overallGainLoss || m.gainLoss,
      (m.overallGainPct != null ? m.overallGainPct : m.gainLossPct || 0).toFixed(2),
      (m.holdingPct || 0).toFixed(2),
      m._metrics?.xirr != null ? m._metrics.xirr.toFixed(2) : "",
      m._metrics?.cagr != null ? m._metrics.cagr.toFixed(2) : "",
      m._metrics?.years || "",
    ]);
    const pdfColumns = ["Member", "Capital", "Units", "Current Value", "Gain/Loss", "Return %", "Holding %", "XIRR", "CAGR"];
    const pdfRows = sorted.map((m) => [
      m.name,
      fmtINR(m.capitalInvested, { decimals: 0 }),
      fmtUnits(m.units),
      fmtINR(m.currentValue, { decimals: 0 }),
      fmtINR(m.gainLoss, { decimals: 0 }),
      (m.gainLossPct || 0).toFixed(2) + "%",
      (m.holdingPct || 0).toFixed(2) + "%",
      m._metrics?.xirr != null ? m._metrics.xirr.toFixed(2) + "%" : "—",
      m._metrics?.cagr != null ? m._metrics.cagr.toFixed(2) + "%" : "—",
    ]);
    return { headers, rows, pdfColumns, pdfRows };
  }, [sorted]);

  if (loading)
    return (
      <AppShell>
        <div className="flex items-center py-24 text-slate-400">
          <Loader2 className="animate-spin mr-2" /> Loading…
        </div>
      </AppShell>
    );

  return (
    <AppShell>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="inline-flex items-center gap-2 text-[10px] text-emerald-300/85 uppercase tracking-[0.22em] font-mono">
            <span className="pulse-dot" style={{ width: 6, height: 6 }} />
            Members
          </div>
          <h1 className="text-2xl md:text-4xl font-display text-white mt-1 tracking-tight">All Family Holdings</h1>
        </div>
        <ExportButtons
          testIdPrefix="members-export"
          disabled={sorted.length === 0}
          csv={{
            filename: stampedName("members"),
            headers: exportData.headers,
            rows: exportData.rows,
          }}
          pdf={{
            title: "Family members — holdings",
            subtitle: `${sorted.length} members`,
            filename: "members",
            orientation: "landscape",
            columns: exportData.pdfColumns,
            rows: exportData.pdfRows,
          }}
        />
      </div>

      {/* Allocation donut */}
      {pieData.length > 0 && (
        <div className="mt-6 bg-slate-900 border border-slate-800 rounded-xl p-5">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="text-sm font-semibold text-white">Portfolio Allocation</div>
              <div className="text-xs text-slate-500">Share of pool by current value</div>
            </div>
          </div>
          <div className="mt-4 grid md:grid-cols-2 gap-4 items-center">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={55}
                    outerRadius={95}
                    paddingAngle={1}
                    stroke="#0f172a"
                  >
                    {pieData.map((entry, idx) => (
                      <Cell key={idx} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12, color: "#e2e8f0" }}
                    formatter={(v) => fmtINR(v)}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {pieData.map((d, idx) => (
                <div key={idx} className="flex items-center gap-2 text-xs">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ background: d.fill }} />
                  <span className="text-slate-300 truncate flex-1">{d.name}</span>
                  <span className="text-slate-500 font-mono">{fmtINR(d.value, { compact: true })}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="mt-5 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={14} />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search members…"
            className="pl-9 bg-slate-900 border-slate-800 text-white placeholder:text-slate-500 focus-visible:ring-emerald-500"
          />
        </div>
        <select
          value={sortKey}
          onChange={(e) => setSortKey(e.target.value)}
          className="bg-slate-900 border border-slate-800 text-white text-sm rounded-md px-3 h-10 focus:outline-none focus:ring-2 focus:ring-emerald-500"
        >
          <option value="holdingPct">Sort: Holding %</option>
          <option value="currentValue">Sort: Current Value</option>
          <option value="capitalInvested">Sort: Capital</option>
          <option value="gainLossPct">Sort: Return %</option>
          <option value="xirr">Sort: XIRR %</option>
          <option value="cagr">Sort: CAGR %</option>
          <option value="units">Sort: Units</option>
        </select>
      </div>

      <div className="mt-5 bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
        <div className="hidden md:block">
          <table className="w-full text-sm">
            <thead className="bg-slate-900/80 text-xs text-slate-400 uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Member</th>
                <th className="text-right px-4 py-3">Capital</th>
                <th className="text-right px-4 py-3">Units</th>
                <th className="text-right px-4 py-3">Current Value</th>
                <th className="text-right px-4 py-3">Gain/Loss</th>
                <th className="text-right px-4 py-3">Holding %</th>
                <th className="text-right px-4 py-3">Return %</th>
                <th className="text-right px-4 py-3" title="Money-weighted return">XIRR</th>
                <th className="text-right px-4 py-3" title="Compound annual growth rate">CAGR</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {sorted.map((m) => {
                const positive = m.gainLoss >= 0;
                const isMe = user?.memberId === m.id;
                const xirr = m._metrics?.xirr;
                const cagr = m._metrics?.cagr;
                return (
                  <tr
                    key={m.id}
                    onClick={() => navigate(`/members/${m.id}`)}
                    data-testid={`member-row-${m.id}`}
                    className={`cursor-pointer hover:bg-slate-800/50 transition-colors ${isMe ? "bg-emerald-500/5" : ""}`}
                  >
                    <td className="px-4 py-3 text-white font-medium">
                      {m.name}
                      {isMe && (
                        <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                          You
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-300 tabular-nums">{fmtINR(m.capitalInvested)}</td>
                    <td className="px-4 py-3 text-right text-slate-300 tabular-nums font-mono">{fmtUnits(m.units)}</td>
                    <td className="px-4 py-3 text-right text-white tabular-nums">{fmtINR(m.currentValue)}</td>
                    <td className={`px-4 py-3 text-right tabular-nums ${positive ? "text-emerald-400" : "text-rose-400"}`}>
                      {fmtINR(m.gainLoss)}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-300 tabular-nums">{fmtPct(m.holdingPct)}</td>
                    <td className={`px-4 py-3 text-right font-medium tabular-nums ${positive ? "text-emerald-400" : "text-rose-400"}`}>
                      {fmtPct(m.gainLossPct)}
                    </td>
                    <td
                      className={`px-4 py-3 text-right tabular-nums font-mono ${
                        xirr == null
                          ? "text-slate-500"
                          : xirr >= 0
                          ? "text-emerald-400"
                          : "text-rose-400"
                      }`}
                    >
                      {xirr != null ? fmtPct(xirr) : "—"}
                    </td>
                    <td
                      className={`px-4 py-3 text-right tabular-nums font-mono ${
                        cagr == null
                          ? "text-slate-500"
                          : cagr >= 0
                          ? "text-emerald-400"
                          : "text-rose-400"
                      }`}
                    >
                      {cagr != null ? fmtPct(cagr) : "—"}
                    </td>
                    <td className="px-2 py-3 text-slate-600">
                      <ChevronRight size={14} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden divide-y divide-slate-800">
          {sorted.map((m) => {
            const positive = m.gainLoss >= 0;
            const isMe = user?.memberId === m.id;
            const xirr = m._metrics?.xirr;
            return (
              <div
                key={m.id}
                onClick={() => navigate(`/members/${m.id}`)}
                data-testid={`member-card-${m.id}`}
                className={`p-4 cursor-pointer active:bg-slate-800/40 ${isMe ? "bg-emerald-500/5" : ""}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-white font-medium text-sm">
                      {m.name}
                      {isMe && (
                        <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                          You
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-1">
                      {fmtPct(m.holdingPct)} of pool
                    </div>
                  </div>
                  <div className={`flex items-center gap-0.5 text-xs ${positive ? "text-emerald-400" : "text-rose-400"}`}>
                    {positive ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                    {fmtPct(m.gainLossPct)}
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-4 gap-2 text-xs">
                  <div>
                    <div className="text-slate-500">Capital</div>
                    <div className="text-slate-200 mt-0.5">{fmtINR(m.capitalInvested, { compact: true })}</div>
                  </div>
                  <div>
                    <div className="text-slate-500">Units</div>
                    <div className="text-slate-200 mt-0.5 font-mono">{fmtUnits(m.units)}</div>
                  </div>
                  <div>
                    <div className="text-slate-500">Current</div>
                    <div className="text-white font-medium mt-0.5">{fmtINR(m.currentValue, { compact: true })}</div>
                  </div>
                  <div>
                    <div className="text-slate-500">XIRR</div>
                    <div
                      className={`mt-0.5 font-mono ${
                        xirr == null ? "text-slate-500" : xirr >= 0 ? "text-emerald-400" : "text-rose-400"
                      }`}
                    >
                      {xirr != null ? fmtPct(xirr) : "—"}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </AppShell>
  );
}
